import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ResizableBox } from 'react-resizable';

import { Add, MoreHoriz } from '@mui/icons-material';
import { Button, Grid, IconButton, Stack, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  incrementRmNameWidth,
  selectRmNameWidth,
  selectRmOthersExpanded,
  selectRmRealtimeOn,
  selectRouteMonitoringHeaders,
  setRMCreateRouteModal,
  setRmOthersExpanded,
  setRmRealtimeOn,
} from 'state/routeMonitoringSlice';
import {
  SavedRouteAndFeature,
  selectCurrentSavedRoutes,
  selectLastUpdated,
} from 'state/savedRouteSlices';
import { ProjectFeatures } from 'state/userSlice';
import {
  selectCurrentProjectInfo,
  selectRouteSelection,
  selectSegmentId,
  setRouteSelection,
  setSelectedSegmentId,
  setTargetDate,
} from 'state/workflowSlice';

import DateTimeSelector from 'features/common/DateTimeSelector/DateTimeSelector';
import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import SearchBar from 'features/common/SearchBar/SearchBar';
import { StlButton } from 'features/common/StlButton/StlButton';
import { nowUnix, parseZonedDateTime } from 'features/common/datetimeUtils';
import {
  routeSelectionIsSavedRoute,
  useRouteInfo,
} from 'features/common/hooks/useRouteInfo';
import { SortOrder, formatTraveltime } from 'features/common/utils';
import LoadingSpinner from 'features/spinner/Spinner';
import { ShareButton } from 'features/task_bar/TaskBarTopbar';
import { MODAL_DESCR } from 'features/workflow_routemonitoring/WorkflowRouteMonitoring';
import { useCurrentRoutesWithGeoJson } from 'features/workflow_routemonitoring/hooks/useRMDataProvider';
import { dimmed_white_color } from 'theme/cemTheme';

import { ReactComponent as AddElements } from '../common/icons/add_elements.svg';
import { AccordionWrapper } from './AccordionWrapper';
import RouteMonitoringSettings from './RouteMonitoringSettings';
import { SortableMonitorListInner } from './SortableMonitorListInner';
import { TemporaryRoute } from './TemporaryRoute';
import { ReactComponent as RealtimeIcon } from './icons/realtime.svg';
import { ReactComponent as Resizer } from './icons/resizer.svg';

import styles from './RouteMonitoringList.module.css';

interface RouteMonitoringListProps {
  lastDate?: string;
}

const REALTIME_WIDTH = 490;
const WIDTH = 708;

export function RouteMonitoringList({ lastDate }: RouteMonitoringListProps) {
  const dispatch = useAppDispatch();
  const [hasStarred, setHasStarred] = useState<boolean>(undefined);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>(undefined);
  const [sortKey, setSortKey] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.NONE);
  const othersExpanded = useAppSelector(selectRmOthersExpanded);
  const currentRoutes = useAppSelector(selectCurrentSavedRoutes);
  const realtime = useAppSelector(selectRmRealtimeOn);
  const lastUpdated = useAppSelector(selectLastUpdated);
  const headers = useAppSelector(selectRouteMonitoringHeaders);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const hasRealtime = userProject.features.includes(ProjectFeatures.REALTIME);
  const segmentId = useAppSelector(selectSegmentId);
  const routeSelection = useAppSelector(selectRouteSelection);
  const nameWidth = useAppSelector(selectRmNameWidth);
  const { isReady } = useCurrentRoutesWithGeoJson();

  const defaultWidth = realtime ? REALTIME_WIDTH : WIDTH;

  const { savedRoute: savedRouteInfo, basicRouteInfo } = useRouteInfo();

  const noRoutesExpected = currentRoutes.length === 0 && !isReady;
  // update segment id once routeSelection is updated
  useEffect(() => {
    const lastSegmentId = basicRouteInfo?.waypointSegments?.at(-1);
    if (segmentId !== lastSegmentId && lastSegmentId) {
      dispatch(setSelectedSegmentId(lastSegmentId));
    }
  }, [basicRouteInfo?.waypointSegments]);

  useEffect(() => {
    setHasStarred(
      currentRoutes?.some((r) => r.savedRoute.starred) ?? undefined,
    );
  }, [currentRoutes]);

  useEffect(() => {
    if (!othersExpanded && savedRouteInfo && !savedRouteInfo.starred)
      dispatch(setRmOthersExpanded(true));
  }, [savedRouteInfo]);

  useEffect(() => {
    if (!othersExpanded && hasStarred === false)
      dispatch(setRmOthersExpanded(true));
  }, [othersExpanded, hasStarred]);

  const handleSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value !== '')
      setSearchTerm(e.target.value.trim().toLowerCase());
    else setSearchTerm(undefined);
  }, []);

  const sortByKey = useCallback((key: string) => {
    setSortKey(key);
    setSortOrder((prev) => {
      switch (prev) {
        case SortOrder.NONE:
          return SortOrder.ASC;
        case SortOrder.ASC:
          return SortOrder.DESC;
        default:
          return SortOrder.NONE;
      }
    });
  }, []);

  const lastUpdatedDiff = useMemo(() => {
    if (!lastUpdated) return undefined;
    return formatTraveltime(
      (nowUnix() - parseZonedDateTime(lastUpdated).valueOf()) / 1000,
      'm [mins]',
    );
  }, [lastUpdated]);

  const renderNavSection = useCallback(() => {
    const handleRealtimeClick = () => {
      const shouldEnableRealtime = !realtime;
      if (shouldEnableRealtime) dispatch(setTargetDate(lastDate));
      dispatch(setRmRealtimeOn(shouldEnableRealtime));
    };

    const handleCreateRoute = () => {
      dispatch(setRMCreateRouteModal(true));
    };

    return (
      <div className={styles.nav_section_wrapper}>
        <Stack direction="row" className={styles.nav_section}>
          <Stack direction="row" justifyContent="flex-start" p={1}>
            <Typography alignSelf="center" variant="body1">
              Time settings
            </Typography>
            <DateTimeSelector disabled={realtime} />
            {hasRealtime && (
              <IconButton
                size="small"
                edge="start"
                title="Realtime"
                className={styles.btn_realtime}
                id={!realtime ? styles.btn_realtime_default : undefined}
                onClick={handleRealtimeClick}
              >
                <RealtimeIcon />
              </IconButton>
            )}
            {realtime && (
              <Button
                startIcon={
                  <FontAwesomeIcon
                    name="clock-rotate-left"
                    sx={{ color: dimmed_white_color }}
                  />
                }
                disabled
                className={styles.btn_last_updated}
              >
                {lastUpdatedDiff && (
                  <Typography color="#fff" variant="caption">
                    Last updated {lastUpdatedDiff} ago
                  </Typography>
                )}
              </Button>
            )}
            <IconButton
              size="small"
              title="More"
              onClick={() => setShowSettings(true)}
            >
              <MoreHoriz />
            </IconButton>
          </Stack>
          <Stack direction="row" justifyContent="flex-end" p={1}>
            <ShareButton />
            <SearchBar searchTerm={searchTerm} onChange={handleSearch} />
            <StlButton
              isActive
              value="Create New Route"
              handleClick={handleCreateRoute}
              icon={<Add />}
              width={156}
            />
          </Stack>
        </Stack>
      </div>
    );
  }, [
    handleSearch,
    realtime,
    searchTerm,
    dispatch,
    hasRealtime,
    lastDate,
    lastUpdatedDiff,
  ]);

  const renderHeader = useCallback(() => {
    const getIcon = (key: string) => {
      if (sortKey === key) {
        if (sortOrder === SortOrder.ASC) {
          return <FontAwesomeIcon name="caret-up" />;
        }
        if (sortOrder === SortOrder.DESC) {
          return <FontAwesomeIcon name="caret-down" />;
        }
      }
      return <FontAwesomeIcon name="sort" type="light" />;
    };
    return (
      <div className={styles.route_header}>
        <Grid container className={styles.route_header_content}>
          {headers.map((el, idx) => (
            <Grid
              key={el.title}
              item
              sx={{ width: idx === 0 ? nameWidth : el.size }}
              onClick={() => sortByKey(el.val)}
              className={styles.route_header_col}
            >
              <Stack sx={{ padding: '0 4px', margin: 'auto 0' }}>
                <div className={styles.icon_wrapper}>
                  <Typography
                    id={styles[`route-header-col-${el.val}`]}
                    className={styles.route_header_col_name}
                  >
                    {el.title}
                  </Typography>
                  <IconButton
                    // key needed- without it the cached icons don't get updated
                    key={`${el.title}-${sortOrder}`}
                    aria-label="sort"
                    title="Sort"
                  >
                    {getIcon(el.val)}
                  </IconButton>
                </div>
                <Typography
                  className={styles.route_header_col_caption}
                  variant="caption"
                >
                  {el.caption || ''}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </div>
    );
  }, [sortKey, sortOrder, sortByKey, headers, nameWidth]);

  const favFilters = useMemo(
    () => ({ starred: true, routeName: searchTerm }),
    [searchTerm],
  );
  const othersFilters = useMemo(
    () => ({ starred: false, routeName: searchTerm }),
    [searchTerm],
  );

  const handleExpanded = (e: React.SyntheticEvent, expanded: boolean) => {
    dispatch(setRmOthersExpanded(expanded));
    if (!expanded) dispatch(setRouteSelection(undefined));
  };

  const showTabs =
    hasStarred ||
    (routeSelection && !routeSelectionIsSavedRoute(routeSelection));

  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!boxRef.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const delta = width - defaultWidth;
        dispatch(incrementRmNameWidth(delta));
      }
    });

    observer.observe(boxRef.current);

    return () => observer.disconnect();
  }, [defaultWidth]);

  return (
    <Stack className={styles.workspace_route_monitoring}>
      <Stack direction="column">{renderNavSection()}</Stack>

      <ResizableBox
        resizeHandles={['e']}
        axis="x"
        className={styles.resizable}
        width={defaultWidth}
        minConstraints={[realtime ? 490 : 708]}
        maxConstraints={[realtime ? 630 : 848]}
        handle={
          <IconButton disableRipple className={styles.resizeHandle}>
            <Resizer />
          </IconButton>
        }
      >
        <div
          ref={boxRef}
          className={styles.list_box}
          id={realtime ? styles.realtime : undefined}
        >
          {noRoutesExpected || currentRoutes.length === 0 ? (
            <div className={styles.no_content_wrapper}>
              {noRoutesExpected ? (
                <LoadingSpinner large />
              ) : (
                <Stack className={styles.no_route_warning}>
                  <AddElements width={209} height={140} />
                  <Typography variant="body2">{MODAL_DESCR}</Typography>
                </Stack>
              )}
            </div>
          ) : (
            <>
              {renderHeader()}
              <div className={styles.list_box_inner}>
                {routeSelection &&
                  !routeSelectionIsSavedRoute(routeSelection) && (
                    <TemporaryRoute />
                  )}
                {showTabs ? (
                  <>
                    {hasStarred && (
                      <AccordionWrapper title="Favorites" allowHide={false}>
                        <SortableMonitorListInner
                          filters={favFilters}
                          sort={sortKey}
                          sortOrder={sortOrder}
                        />
                      </AccordionWrapper>
                    )}
                    {currentRoutes.some(
                      (el: SavedRouteAndFeature) => !el.savedRoute.starred,
                    ) && (
                      <AccordionWrapper
                        title="Others"
                        expanded={othersExpanded}
                        handleExpanded={handleExpanded}
                      >
                        <SortableMonitorListInner
                          filters={othersFilters}
                          sort={sortKey}
                          sortOrder={sortOrder}
                        />
                      </AccordionWrapper>
                    )}
                  </>
                ) : (
                  othersExpanded && (
                    <SortableMonitorListInner
                      filters={othersFilters}
                      sort={sortKey}
                      sortOrder={sortOrder}
                    />
                  )
                )}
              </div>
            </>
          )}
        </div>
      </ResizableBox>

      <RouteMonitoringSettings
        isOpen={showSettings}
        setIsOpen={setShowSettings}
      />
    </Stack>
  );
}
